import type { PatientDTO } from '@/generated'
import { parse } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { useI18n } from 'vue-i18n'
import { translateGenderDTO } from '@/util/translations'
import { useFormatDate } from '@/composables/useFormatDate'

function useFormattedPatientDetails() {
  const { locale, t } = useI18n()
  const { formatDateOfBirth } = useFormatDate()

  function getFormattedAge(patient: PatientDTO) {
    const yearsTranslated = locale.value === 'de' ? t('Years') : t('Years').toLowerCase()
    return `*${getDateOfBirthFormatted(patient?.dateOfBirth)} (${getPatientAge(patient.dateOfBirth)} ${yearsTranslated})`
  }

  function getDateOfBirthFormatted(dateString: string | undefined | null): string {
    if (!dateString) return ''
    const dob = parse(dateString, 'yyyy-MM-dd', new UTCDate())
    return formatDateOfBirth(dob)
  }

  function getPatientAge(dateOfBirth?: string | null): string {
    if (!dateOfBirth) {
      return ''
    }

    const d = new Date(dateOfBirth)
    if (isNaN(d.getTime())) {
      return ''
    }

    const today = new Date()
    let age = today.getFullYear() - d.getFullYear()

    if (today < new Date(d.setFullYear(today.getFullYear()))) {
      age--
    }
    return age.toString()
  }

  function getFormattedPatientName(patient: PatientDTO, showAge: boolean = true): string {
    if (patient.dateOfBirth && showAge) {
      return `${patient.lastName}, ${patient.firstName} (${translateGenderDTO(patient.gender, t)}, ${getPatientAge(patient.dateOfBirth)})`
    }

    return `${patient.lastName}, ${patient.firstName} (${translateGenderDTO(patient.gender, t)})`
  }

  return {
    getFormattedAge,
    getFormattedPatientName
  }
}

export default useFormattedPatientDetails
